/**
 * Run Detail page — full view of a single ScriptExecutionLog.
 *
 * Route: /scripts/:id/runs/:execId
 * Data:  GET /api/extension-scripts/:id  + GET /api/extension-scripts/executions/:execId
 *
 * Shows:
 *   - Header: status + duration + timestamp + trigger + cred + re-run button
 *   - Video (if present) with clickable step-marker sidebar (seek on click)
 *   - Per-step detail table
 *   - Final URL
 *   - Error summary (for failed runs)
 *   - AI analysis block (verdict + summary + suggestions)
 *   - Logs / raw metadata pane (collapsible)
 *
 * Arriving here: from "Open full run detail →" links in the History tab
 * expanded rows. Fully deep-linkable.
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Alert, Tooltip,
  IconButton, Collapse, Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Replay as ReplayIcon,
  OpenInNew as ExternalIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  RadioButtonUnchecked as SkippedIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import extensionScriptsService from '../services/api/extensionScriptsService';
import { ExecutorModeChip, resolveExecutorMode } from '../components/ExecutorModeControls';

const statusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'passed' || s === 'completed') return '#16a34a';
  if (s === 'failed') return '#dc2626';
  if (s === 'skipped') return '#94a3b8';
  return '#64748b';
};

const formatDuration = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

/**
 * Heuristic: place step markers on the video timeline by cumulative duration
 * relative to the run total. Good enough for a v1 nav sidebar.
 */
const computeStepMarkers = (stepResults = [], videoDurationSec = 0) => {
  if (!stepResults.length || !videoDurationSec) return [];
  const totalMs = stepResults.reduce((a, r) => a + (r.durationMs || 0), 0);
  if (totalMs <= 0) return [];
  let cum = 0;
  return stepResults.map((r, i) => {
    const startFrac = cum / totalMs;
    cum += (r.durationMs || 0);
    return {
      step: r.step ?? i + 1,
      name: r.stepName || r.name || `Step ${r.step || i + 1}`,
      status: r.status,
      durationMs: r.durationMs || 0,
      seekSec: startFrac * videoDurationSec,
    };
  });
};

const RunDetailPage = () => {
  const { id: scriptId, execId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [script, setScript] = useState(null);
  const [exec, setExec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [rawExpanded, setRawExpanded] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunResult, setRerunResult] = useState(null);
  const viewLoggedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        // Fetch script + the specific execution log in parallel.
        const [scriptResp, execResp] = await Promise.all([
          extensionScriptsService.get(scriptId),
          extensionScriptsService.getExecution(execId),
        ]);
        if (cancelled) return;
        setScript(scriptResp.data);
        setExec(execResp.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Failed to load run');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scriptId, execId]);

  const markers = useMemo(
    () => computeStepMarkers(exec?.stepResults || [], videoDuration),
    [exec, videoDuration],
  );

  const passed = exec && (exec.status === 'passed' || exec.status === 'completed');
  const videoUrl = exec?.metadata?.videoUrl;
  const finalUrl = exec?.metadata?.finalUrl;
  const ai = exec?.metadata?.aiAnalysis;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 6, gap: 1.5 }}>
        <CircularProgress size={22} />
        <Typography color="text.secondary">Loading run…</Typography>
      </Box>
    );
  }
  if (error || !exec) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/scripts/${scriptId}/edit`)}>Back to script</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error || 'Run not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button size="small" startIcon={<BackIcon />} onClick={() => navigate(`/scripts/${scriptId}/edit`)}>
        Back to {script?.name || 'script'}
      </Button>

      {/* Header strip */}
      <Paper variant="outlined" sx={{ p: 2, mt: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: '50%',
            background: passed ? '#dcfce7' : '#fee2e2',
          }}>
            {passed ? <PassIcon sx={{ color: '#16a34a' }} /> : <FailIcon sx={{ color: '#dc2626' }} />}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Run {exec.id.substring(0, 8)}… <span style={{ color: statusColor(exec.status), fontWeight: 700, textTransform: 'capitalize' }}>{exec.status}</span>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {script?.name} · {new Date(exec.startedAt).toLocaleString()} · {formatDuration(exec.durationMs)} · {exec.stepsCompleted}/{exec.stepsTotal} steps
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip size="small" label={exec.trigger || '—'} sx={{ textTransform: 'capitalize' }} />
            <ExecutorModeChip exec={exec} />
            {exec.credentialVaultId && (
              <Tooltip title={exec.credentialVaultId}>
                <Chip size="small" label={`cred: ${exec.credentialVaultId.substring(0, 8)}…`} variant="outlined" />
              </Tooltip>
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={rerunning ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
              disabled={rerunning}
              onClick={async () => {
                setRerunning(true); setRerunResult(null);
                try {
                  // Re-run with the same executor that produced the original
                  // run when known, falling back to Playwright (the default).
                  const mode = resolveExecutorMode(exec);
                  const { data } = await extensionScriptsService.runByExecutor(scriptId, mode, {
                    credentialVaultId: exec.credentialVaultId,
                    recordMode: 'full',
                  });
                  setRerunResult({ ok: true, execId: data?.executionLogId });
                  if (data?.executionLogId) {
                    navigate(`/scripts/${scriptId}/runs/${data.executionLogId}`);
                  }
                } catch (err) {
                  setRerunResult({ ok: false, error: err?.response?.data?.message || err?.message || 'Re-run failed' });
                } finally {
                  setRerunning(false);
                }
              }}
            >
              Re-run
            </Button>
          </Box>
        </Box>
        {rerunResult && !rerunResult.ok && <Alert severity="error" sx={{ mt: 1 }}>{rerunResult.error}</Alert>}
      </Paper>

      {/* Video + step markers sidebar */}
      {videoUrl ? (
        <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 0 }}>
            <Box sx={{ background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                style={{ maxWidth: '100%', maxHeight: 520, width: '100%' }}
                onLoadedMetadata={(e) => setVideoDuration(e.target.duration || 0)}
                onPlay={() => {
                  // Audit log: record one view per mount (R-3e). Guard against
                  // pause-then-play triggering a second record for the same mount.
                  if (viewLoggedRef.current) return;
                  viewLoggedRef.current = true;
                  extensionScriptsService.recordVideoView(execId).catch(() => {});
                }}
              />
            </Box>
            <Box sx={{ p: 1.5, borderLeft: '1px solid #e2e8f0', maxHeight: 520, overflowY: 'auto' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 1 }}>
                Step timeline {videoDuration > 0 && `(${videoDuration.toFixed(1)}s)`}
              </Typography>
              {typeof exec.metadata?.videoViewCount === 'number' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: 10 }}>
                  👁 Viewed {exec.metadata.videoViewCount} time{exec.metadata.videoViewCount === 1 ? '' : 's'}
                </Typography>
              )}
              {markers.length === 0 ? (
                <Typography variant="caption" color="text.secondary">Waiting for video metadata…</Typography>
              ) : markers.map((m) => (
                <Box
                  key={m.step}
                  onClick={() => { if (videoRef.current) { videoRef.current.currentTime = m.seekSec; videoRef.current.play?.(); } }}
                  sx={{
                    p: 0.75, borderRadius: 0.75, cursor: 'pointer',
                    background: m.status === 'failed' ? '#fef2f2' : m.status === 'skipped' ? '#f8fafc' : 'white',
                    border: '1px solid',
                    borderColor: m.status === 'failed' ? '#fecaca' : '#e2e8f0',
                    mb: 0.5,
                    display: 'grid', gridTemplateColumns: '16px 24px 1fr 42px',
                    gap: 0.75, alignItems: 'center', fontSize: 12.5,
                    '&:hover': { borderColor: '#94a3b8' },
                  }}
                >
                  {m.status === 'passed' ? <PassIcon sx={{ color: '#16a34a', fontSize: 14 }} />
                   : m.status === 'failed' ? <FailIcon sx={{ color: '#dc2626', fontSize: 14 }} />
                   : <SkippedIcon sx={{ color: '#94a3b8', fontSize: 14 }} />}
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{m.step}</Typography>
                  <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: 10, textAlign: 'right' }}>
                    {m.seekSec >= 0 ? `${m.seekSec.toFixed(1)}s` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          No video captured for this run. Recording mode was <b>{exec.metadata?.recordMode || 'failures_only'}</b> and the run {passed ? 'passed (failures-only mode discards successful videos)' : 'did not produce a video — check script and recording config'}.
        </Alert>
      )}

      {/* AI analysis */}
      {ai && (
        <Paper variant="outlined" sx={{
          mb: 2, p: 2,
          background: 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)',
          borderColor: '#e9d5ff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: 0.5,
              background: 'linear-gradient(135deg, #a855f7, #6366f1)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 12, fontWeight: 700,
            }}>✦</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6b21a8' }}>AI analysis</Typography>
            <Typography variant="caption" sx={{ ml: 'auto', color: '#a78bfa' }}>
              {ai.model} · {formatDuration(ai.durationMs)} {ai.promptDigest && `· digest ${ai.promptDigest.substring(0, 18)}`}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: passed ? '#065f46' : '#991b1b', mb: 1 }}>
            {passed ? '✓ ' : '✗ '}{ai.verdict}
          </Typography>
          <Typography variant="body2" sx={{ color: '#312e81', lineHeight: 1.55 }}>
            {ai.summary}
          </Typography>
          {Array.isArray(ai.suggestions) && ai.suggestions.length > 0 && (
            <Box sx={{ mt: 1.5, p: 1.5, background: 'white', border: '1px solid #e9d5ff', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Recommended fixes
              </Typography>
              <Box component="ol" sx={{ mt: 0.5, mb: 0, pl: 3, color: '#1e293b', '& li': { mt: 0.5 } }}>
                {ai.suggestions.map((s, i) => <li key={i}><Typography variant="body2">{s}</Typography></li>)}
              </Box>
            </Box>
          )}
          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #e9d5ff', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="small" label="PII-free" sx={{ height: 18, background: 'white', color: '#4338ca', fontWeight: 700, border: '1px solid #c7d2fe' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              Analysis scope: automation mechanics only (selectors, timing, step flow). User content NOT transcribed.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Step results */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" fontWeight={700}>Step results</Typography>
        </Box>
        {(exec.stepResults || []).map((r, i, arr) => (
          <Box
            key={i}
            sx={{
              display: 'grid', gridTemplateColumns: '24px 40px 1fr 90px 2fr',
              gap: 1.5, px: 2, py: 1, alignItems: 'center',
              borderBottom: i < arr.length - 1 ? '1px solid #eef2f6' : 'none',
              background: r.status === 'failed' ? '#fef2f2' : r.status === 'skipped' ? '#f8fafc' : 'white',
            }}
          >
            {r.status === 'passed' ? <PassIcon sx={{ color: '#16a34a', fontSize: 18 }} />
             : r.status === 'failed' ? <FailIcon sx={{ color: '#dc2626', fontSize: 18 }} />
             : <SkippedIcon sx={{ color: '#94a3b8', fontSize: 18 }} />}
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{r.step}</Typography>
            <Typography variant="body2" sx={{ color: r.status === 'skipped' ? 'text.secondary' : 'inherit' }}>
              {r.stepName || r.name || `Step ${r.step}`}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', textAlign: 'right' }}>
              {formatDuration(r.durationMs)}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#dc2626', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.error || ''}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Final URL */}
      {finalUrl && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Final URL
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {finalUrl}
          </Typography>
          <IconButton size="small" href={finalUrl} target="_blank" rel="noopener noreferrer">
            <ExternalIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {/* Error */}
      {exec.error && <Alert severity="error" sx={{ mb: 2 }}><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{exec.error}</Typography></Alert>}

      {/* Raw metadata (admin / debug) */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setRawExpanded((v) => !v)}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>Raw execution record</Typography>
          <ExpandIcon sx={{ transform: rawExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </Box>
        <Collapse in={rawExpanded}>
          <Divider />
          <Box component="pre" sx={{
            p: 1.5, m: 0, fontFamily: 'monospace', fontSize: 11, overflow: 'auto',
            background: '#f8fafc', maxHeight: 400,
          }}>
            {JSON.stringify(exec, null, 2)}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default RunDetailPage;
